import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import logo from '../../static/BSC Logos.png'
import './HomePage.css'

// ── Types ──────────────────────────────────────
interface Star {
  id: number
  x: number
  y: number
  size: number
  dur: number
  delay: number
}

// ── Data ───────────────────────────────────────
const NAV_LINKS = [
  { label: 'Serviços',     id: 'servicos' },
  { label: 'Sobre',        id: 'sobre' },
  { label: 'Tecnologias',  id: 'tecnologias' },
  { label: 'Contato',      id: 'contato' },
]

const SERVICES = [
  {
    icon: 'pi pi-code',
    title: 'Desenvolvimento Web',
    desc: 'Aplicações modernas, rápidas e responsivas. Da landing page ao sistema corporativo complexo, com arquitetura que escala.',
  },
  {
    icon: 'pi pi-mobile',
    title: 'Aplicativos Mobile',
    desc: 'Apps nativos e híbridos para iOS e Android com experiência de usuário fluida e integração com sistemas existentes.',
  },
  {
    icon: 'pi pi-server',
    title: 'Backend & APIs',
    desc: 'APIs robustas e bem documentadas. Microsserviços, integrações e bancos de dados otimizados para alto desempenho.',
  },
  {
    icon: 'pi pi-cloud',
    title: 'Cloud & DevOps',
    desc: 'Infraestrutura como código, CI/CD automatizado, monitoramento e deploy contínuo para garantir disponibilidade.',
  },
  {
    icon: 'pi pi-chart-line',
    title: 'Consultoria Técnica',
    desc: 'Arquitetura de sistemas, revisão de código, escolha de stack e roadmap tecnológico alinhado ao seu negócio.',
  },
  {
    icon: 'pi pi-shield',
    title: 'Manutenção & Suporte',
    desc: 'Monitoramento proativo, atualizações de segurança e suporte dedicado para manter seus sistemas sempre saudáveis.',
  },
]

const PILLARS = [
  {
    icon: 'pi pi-star',
    title: 'Qualidade antes de velocidade',
    desc: 'Código revisado, testado e documentado. Entregamos certo na primeira vez.',
  },
  {
    icon: 'pi pi-comments',
    title: 'Comunicação direta',
    desc: 'Sem intermediários. Você fala com quem está construindo.',
  },
  {
    icon: 'pi pi-refresh',
    title: 'Iteração contínua',
    desc: 'Entregamos em ciclos curtos, validamos e evoluímos junto com você.',
  },
  {
    icon: 'pi pi-lock',
    title: 'Compromisso com o resultado',
    desc: 'Não some após a entrega. Estamos aqui para o longo prazo.',
  },
]

const STACK_ITEMS = [
  { label: 'React', color: '#61dafb' },
  { label: 'TypeScript', color: '#3178c6' },
  { label: 'Node.js', color: '#5fa04e' },
  { label: '.NET / C#', color: '#512bd4' },
  { label: 'PostgreSQL', color: '#336791' },
  { label: 'SQL Server', color: '#cc2927' },
  { label: 'Docker', color: '#2496ed' },
  { label: 'Azure', color: '#0089d6' },
  { label: 'React Native', color: '#61dafb' },
  { label: 'Next.js', color: '#c8d6e5' },
  { label: 'Redis', color: '#dc382c' },
  { label: 'SignalR', color: '#512bd4' },
]

const ABOUT_CARD_ITEMS = [
  { label: 'Frontend Web',    color: '#61dafb' },
  { label: 'Backend APIs',    color: '#5fa04e' },
  { label: 'Mobile Apps',     color: '#a78bfa' },
  { label: 'Cloud & Infra',   color: '#f59e0b' },
  { label: 'Segurança',       color: '#34d399' },
]

// ── Utilities ──────────────────────────────────
function generateStars(count: number): Star[] {
  return Array.from({ length: count }, (_, i) => ({
    id: i,
    x: Math.random() * 100,
    y: Math.random() * 100,
    size: Math.random() * 2 + 0.5,
    dur: Math.random() * 4 + 2,
    delay: Math.random() * 5,
  }))
}

function scrollTo(id: string) {
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' })
}

// ── Reveal hook ────────────────────────────────
function useReveal() {
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const el = ref.current
    if (!el) return
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          el.querySelectorAll('.bsc-reveal').forEach((node) => {
            node.classList.add('visible')
          })
          obs.disconnect()
        }
      },
      { threshold: 0.12 }
    )
    obs.observe(el)
    return () => obs.disconnect()
  }, [])

  return ref
}

// ── Stars component ────────────────────────────
const Stars: React.FC = () => {
  const [stars] = useState(() => generateStars(80))

  return (
    <div className='bsc-hero-stars'>
      {stars.map((s) => (
        <div
          key={s.id}
          className='bsc-star'
          style={{
            left: `${s.x}%`,
            top: `${s.y}%`,
            width: s.size,
            height: s.size,
            ['--dur' as string]: `${s.dur}s`,
            ['--delay' as string]: `${s.delay}s`,
          }}
        />
      ))}
    </div>
  )
}

// ── Service card with mouse glow ───────────────
const ServiceCard: React.FC<{ service: typeof SERVICES[0]; delay?: string }> = ({ service, delay }) => {
  const ref = useRef<HTMLDivElement>(null)

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const card = ref.current
    if (!card) return
    const rect = card.getBoundingClientRect()
    const mx = ((e.clientX - rect.left) / rect.width) * 100
    const my = ((e.clientY - rect.top) / rect.height) * 100
    card.style.setProperty('--mx', `${mx}%`)
    card.style.setProperty('--my', `${my}%`)
  }

  return (
    <div
      ref={ref}
      className={`bsc-service-card bsc-reveal${delay ? ` bsc-reveal-delay-${delay}` : ''}`}
      onMouseMove={handleMouseMove}
    >
      <div className='bsc-service-icon-wrap'>
        <i className={`${service.icon} bsc-service-icon`} />
      </div>
      <h3 className='bsc-service-title'>{service.title}</h3>
      <p className='bsc-service-desc'>{service.desc}</p>
    </div>
  )
}

// ── Main component ─────────────────────────────
const HomePage: React.FC = () => {
  const navigate = useNavigate()
  const [scrolled, setScrolled] = useState(false)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [form, setForm] = useState({ nome: '', email: '', telefone: '', mensagem: '' })

  const servicesRef = useReveal()
  const aboutRef = useReveal()
  const contactRef = useReveal()

  useEffect(() => {
    const handler = () => {
      setScrolled(window.scrollY > 32)
      if (window.scrollY > 32) setDrawerOpen(false)
    }
    window.addEventListener('scroll', handler, { passive: true })
    return () => window.removeEventListener('scroll', handler)
  }, [])

  const handleNavClick = (id: string) => {
    setDrawerOpen(false)
    scrollTo(id)
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    console.log('Contact form:', form)
  }

  const doubledStack = [...STACK_ITEMS, ...STACK_ITEMS]

  return (
    <div className='bsc-landing'>
      {/* ── Navigation ── */}
      <nav className={`bsc-nav${scrolled ? ' scrolled' : ''}`}>
        <a className='bsc-nav-logo' href='/'>
          <img src={logo} alt='BSC Tecnologia' />
        </a>
        <ul className='bsc-nav-links'>
          {NAV_LINKS.map((link) => (
            <li key={link.id}>
              <button className='bsc-nav-link' onClick={() => scrollTo(link.id)}>
                {link.label}
              </button>
            </li>
          ))}
        </ul>
        <button className='bsc-nav-cta' onClick={() => navigate('/login')}>
          Área do Cliente
        </button>
        <button
          className='bsc-nav-hamburger'
          onClick={() => setDrawerOpen((v) => !v)}
          aria-label='Menu'
        >
          <i className={drawerOpen ? 'pi pi-times' : 'pi pi-bars'} />
        </button>
      </nav>

      <div className={`bsc-nav-drawer${drawerOpen ? ' open' : ''}`}>
        {NAV_LINKS.map((link) => (
          <button key={link.id} className='bsc-nav-link' onClick={() => handleNavClick(link.id)}>
            {link.label}
          </button>
        ))}
        <button className='bsc-nav-cta' onClick={() => { setDrawerOpen(false); navigate('/login') }}>
          Área do Cliente
        </button>
      </div>

      {/* ── Hero ── */}
      <section className='bsc-hero'>
        <Stars />

        <div className='bsc-hero-content'>
          <div className='bsc-hero-badge'>
            <span className='bsc-hero-badge-dot' />
            BSC Soluções em Tecnologia
          </div>

          <h1 className='bsc-hero-title'>
            Seu projeto merece{' '}
            <br />
            <span className='bsc-hero-title-accent'>software de verdade</span>
          </h1>

          <p className='bsc-hero-sub'>
            Desenvolvemos sistemas web, mobile e APIs com qualidade de produto —
            não de agência. Da ideia à produção, com quem entende do negócio.
          </p>

          <div className='bsc-hero-ctas'>
            <button className='bsc-btn-primary' onClick={() => scrollTo('contato')}>
              <i className='pi pi-arrow-right' />
              Fale com a gente
            </button>
            <button className='bsc-btn-outline' onClick={() => scrollTo('servicos')}>
              <i className='pi pi-briefcase' />
              Ver serviços
            </button>
          </div>
        </div>

        <div className='bsc-scroll-hint'>
          <div className='bsc-scroll-mouse' />
          <span>scroll</span>
        </div>
      </section>

      {/* ── Services ── */}
      <section id='servicos' className='bsc-section bsc-section-alt'>
        <div className='bsc-container'>
          <div className='bsc-section-header'>
            <span className='bsc-section-label'>O que fazemos</span>
            <h2 className='bsc-section-title'>Serviços</h2>
            <div className='bsc-glow-divider' />
            <p className='bsc-section-desc'>
              Tecnologia aplicada ao problema certo. Sem over-engineering, sem atalhos.
            </p>
          </div>

          <div className='bsc-services-grid' ref={servicesRef}>
            {SERVICES.map((service, i) => (
              <ServiceCard
                key={service.title}
                service={service}
                delay={String((i % 4) + 1) as any}
              />
            ))}
          </div>
        </div>
      </section>

      {/* ── About ── */}
      <section id='sobre' className='bsc-section'>
        <div className='bsc-container'>
          <div className='bsc-about-grid' ref={aboutRef}>
            <div className='bsc-about-text'>
              <span className='bsc-section-label bsc-reveal'>Sobre a BSC</span>
              <h2 className='bsc-section-title bsc-reveal bsc-reveal-delay-1'>
                Construímos com responsabilidade
              </h2>
              <div className='bsc-glow-divider' style={{ margin: '1.5rem 0' }} />
              <p className='bsc-section-desc bsc-reveal bsc-reveal-delay-2'>
                Somos uma software house focada em entregar software que funciona, escala
                e é fácil de manter. Trabalhamos com empresas que precisam de um parceiro
                técnico de verdade — não apenas de um fornecedor.
              </p>

              <div className='bsc-pillars'>
                {PILLARS.map((p, i) => (
                  <div key={p.title} className={`bsc-pillar bsc-reveal bsc-reveal-delay-${i + 1}`}>
                    <i className={`${p.icon} bsc-pillar-icon`} />
                    <div className='bsc-pillar-text'>
                      <strong>{p.title}</strong>
                      <span>{p.desc}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className='bsc-about-visual'>
              <div className='bsc-about-card bsc-reveal bsc-reveal-delay-2'>
                <p className='bsc-about-card-title'>Áreas de atuação</p>
                {ABOUT_CARD_ITEMS.map((item) => (
                  <div key={item.label} className='bsc-stack-item'>
                    <span
                      className='bsc-stack-dot'
                      style={{ background: item.color, boxShadow: `0 0 8px ${item.color}` }}
                    />
                    {item.label}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── Technologies ── */}
      <section id='tecnologias' className='bsc-section bsc-section-alt'>
        <div className='bsc-container'>
          <div className='bsc-section-header'>
            <span className='bsc-section-label'>Stack</span>
            <h2 className='bsc-section-title'>Tecnologias que dominamos</h2>
            <div className='bsc-glow-divider' />
            <p className='bsc-section-desc'>
              Ferramentas modernas e comprovadas no mercado — escolhidas pelo problema, não pela moda.
            </p>
          </div>
        </div>

        <div className='bsc-marquee-wrapper' style={{ marginTop: '3rem' }}>
          <div className='bsc-marquee-track'>
            {doubledStack.map((tech, i) => (
              <div key={`${tech.label}-${i}`} className='bsc-tech-chip'>
                <span
                  className='bsc-tech-chip-dot'
                  style={{ background: tech.color, boxShadow: `0 0 6px ${tech.color}` }}
                />
                {tech.label}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Contact ── */}
      <section id='contato' className='bsc-section'>
        <div className='bsc-container'>
          <div className='bsc-section-header'>
            <span className='bsc-section-label'>Fale conosco</span>
            <h2 className='bsc-section-title'>Vamos conversar?</h2>
            <div className='bsc-glow-divider' />
            <p className='bsc-section-desc'>
              Tem um projeto em mente? Conta pra gente. Sem compromisso, sem pressão.
            </p>
          </div>

          <div className='bsc-contact-grid' ref={contactRef}>
            <div className='bsc-contact-info'>
              <a href='mailto:contato@bsctecnologia.com.br' className='bsc-contact-item bsc-reveal'>
                <div className='bsc-contact-item-icon'>
                  <i className='pi pi-envelope' />
                </div>
                contato@bsctecnologia.com.br
              </a>
              <a href='tel:+5531994581234' className='bsc-contact-item bsc-reveal bsc-reveal-delay-1'>
                <div className='bsc-contact-item-icon'>
                  <i className='pi pi-phone' />
                </div>
                (31) 99458-1234
              </a>
              <a
                href='https://wa.me/5531994581234'
                className='bsc-contact-item bsc-reveal bsc-reveal-delay-2'
                target='_blank'
                rel='noreferrer'
              >
                <div className='bsc-contact-item-icon'>
                  <i className='pi pi-whatsapp' />
                </div>
                WhatsApp direto
              </a>

              <div className='bsc-reveal bsc-reveal-delay-3' style={{ marginTop: '1rem' }}>
                <p style={{ color: 'var(--bsc-silver-dim)', fontSize: '0.85rem', lineHeight: 1.6 }}>
                  Belo Horizonte, MG<br />
                  Atendimento presencial e remoto
                </p>
              </div>
            </div>

            <form className='bsc-form bsc-reveal' onSubmit={handleSubmit}>
              <div className='bsc-form-row'>
                <div className='bsc-form-group'>
                  <label className='bsc-form-label' htmlFor='c-nome'>Nome</label>
                  <input
                    id='c-nome'
                    className='bsc-form-input'
                    placeholder='Seu nome'
                    value={form.nome}
                    onChange={(e) => setForm({ ...form, nome: e.target.value })}
                    required
                  />
                </div>
                <div className='bsc-form-group'>
                  <label className='bsc-form-label' htmlFor='c-email'>E-mail</label>
                  <input
                    id='c-email'
                    type='email'
                    className='bsc-form-input'
                    placeholder='seu@email.com'
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className='bsc-form-group'>
                <label className='bsc-form-label' htmlFor='c-tel'>Telefone (opcional)</label>
                <input
                  id='c-tel'
                  type='tel'
                  className='bsc-form-input'
                  placeholder='(00) 00000-0000'
                  value={form.telefone}
                  onChange={(e) => setForm({ ...form, telefone: e.target.value })}
                />
              </div>

              <div className='bsc-form-group'>
                <label className='bsc-form-label' htmlFor='c-msg'>Mensagem</label>
                <textarea
                  id='c-msg'
                  className='bsc-form-textarea'
                  placeholder='Me conta sobre seu projeto...'
                  value={form.mensagem}
                  onChange={(e) => setForm({ ...form, mensagem: e.target.value })}
                  required
                />
              </div>

              <button type='submit' className='bsc-btn-primary' style={{ justifyContent: 'center' }}>
                <i className='pi pi-send' />
                Enviar mensagem
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* ── Footer ── */}
      <footer className='bsc-footer'>
        <div className='bsc-footer-brand'>
          <img src={logo} alt='BSC' />
          BSC Soluções em Tecnologia
        </div>
        <p className='bsc-footer-copy'>
          © {new Date().getFullYear()} BSC Tecnologia. Todos os direitos reservados.
        </p>
      </footer>
    </div>
  )
}

export default HomePage
